/* ==========================================================================
   Shapiano — theme.js
   Vanilla custom elements, no framework. Everything talks to standard
   Shopify storefront endpoints: /cart/add.js, /cart.js, /cart/change.js,
   /search/suggest.json. Nothing here depends on an SPA or custom backend.
   ========================================================================== */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const routes = window.routes || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const money = (cents) => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    // Shopify money formats use {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}}…
    return String(window.theme?.moneyFormat || '£{{amount}}').replace(/<[^>]*>/g, '').replace(/{{\s*(\w+)\s*}}/, (_, k) =>
      k === 'amount_no_decimals' ? Math.round(Number(amount)).toString() : k.includes('comma') ? amount.replace('.', ',') : amount);
  };
  const escapeHTML = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  function toast(message) {
    let el = $('.toast');
    if (!el) { el = document.createElement('div'); el.className = 'toast'; el.setAttribute('role', 'status'); document.body.append(el); }
    el.textContent = message;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 3200);
  }
  window.themeToast = toast;

  /* ---------------------------------------------------------------- header */
  // Adds a hairline once scrolled; tucks away when scrolling down, returns when scrolling up.
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const wrapper = this.closest('.shopify-section') || this;
      let lastY = window.scrollY;
      let ticking = false;
      const update = () => {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 8);
        const drawerOpen = document.body.classList.contains('has-drawer');
        if (!drawerOpen && Math.abs(y - lastY) > 6) wrapper.classList.toggle('is-tucked', y > lastY && y > 320);
        if (y < 320) wrapper.classList.remove('is-tucked');
        lastY = y;
        ticking = false;
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      this.addEventListener('focusin', () => wrapper.classList.remove('is-tucked'));
      update();
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------------------------------------------------------------- drawers */
  class ModalDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });
    }
    get focusables() { return $$('a[href], button:not([disabled]), input:not([type=hidden]), select, textarea, iframe, [tabindex]:not([tabindex="-1"])', this.panel).filter((el) => el.offsetParent !== null); }
    get panel() { return $('.drawer__panel', this); }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-drawer');
      $$(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const target = $('[data-autofocus]', this) || this.focusables[0];
      setTimeout(() => target?.focus({ preventScroll: true }), reduceMotion ? 0 : 120);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!$('.drawer.is-open')) document.body.classList.remove('has-drawer');
      $$(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.opener?.focus?.({ preventScroll: true });
    }
    trapFocus(e) {
      const f = this.focusables; if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  customElements.define('modal-drawer', ModalDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); document.getElementById(opener.dataset.drawerOpen)?.open(opener); }
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle && window.theme?.cartType === 'drawer' && document.getElementById('CartDrawer')) {
      e.preventDefault(); document.getElementById('CartDrawer').open(cartToggle);
    }
  });

  /* ---------------------------------------------------------------- cart */
  const Cart = {
    async get() { return (await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } })).json(); },
    async add(formData) {
      const res = await fetch(`${routes.cart_add}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${routes.cart_change}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
      return res.json();
    },
    publish(cart) {
      $$('[data-cart-count]').forEach((el) => {
        const grew = cart.item_count > Number(el.textContent || 0);
        el.textContent = cart.item_count; el.hidden = cart.item_count === 0;
        if (grew) { el.classList.remove('is-bumping'); void el.offsetWidth; el.classList.add('is-bumping'); }
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    },
  };
  window.ThemeCart = Cart;

  function renderLines(cart) {
    if (!cart.item_count) {
      return `<div class="cart-empty"><p>${escapeHTML('Nothing here yet.')}</p>
        <a class="btn btn--outline" href="/collections/commissions">Browse commissions</a>
        <a class="link-arrow" href="/collections/sheets"><span>Sheet music</span></a></div>`;
    }
    const hasCommission = cart.items.some((i) => i.product_type === 'Commission');
    const lines = cart.items.map((item, idx) => {
      const opts = (item.options_with_values || []).filter((o) => o.value !== 'Default Title').map((o) => `<span>${escapeHTML(o.name.charAt(0) + o.name.slice(1).toLowerCase())}: ${escapeHTML(o.value)}</span>`).join('');
      const props = Object.entries(item.properties || {}).filter(([k, v]) => v && !k.startsWith('_')).map(([k, v]) => `<p class="cart-line__prop"><strong>${escapeHTML(k)}:</strong> ${escapeHTML(v)}</p>`).join('');
      return `<div class="cart-line" data-line="${idx + 1}">
        <a class="cart-line__media" href="${item.url}">${item.image ? `<img src="${item.image}" alt="" width="88" height="88" loading="lazy">` : ''}</a>
        <div>
          <a class="cart-line__title" href="${item.url}">${escapeHTML(item.product_title)}</a>
          <div class="cart-line__meta">${opts}<span class="tabular">${money(item.final_price)}</span></div>
          ${props}
          <div class="cart-line__row">
            <div class="qty" role="group" aria-label="Quantity for ${escapeHTML(item.product_title)}">
              <button type="button" data-qty="-1" aria-label="Decrease">${ICON_MINUS}</button>
              <input type="number" min="0" value="${item.quantity}" inputmode="numeric" data-qty-input aria-label="Quantity">
              <button type="button" data-qty="1" aria-label="Increase">${ICON_PLUS}</button>
            </div>
            <span class="tabular">${money(item.final_line_price)}</span>
          </div>
          <button class="cart-line__remove" type="button" data-remove>Remove</button>
        </div>
      </div>`;
    }).join('');
    const note = hasCommission && window.theme?.commissionNote ? `<p class="cart-commission-note">${escapeHTML(window.theme.commissionNote)}</p>` : '';
    return lines + note;
  }
  const ICON_MINUS = '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 12h12"/></svg>';
  const ICON_PLUS = '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 6v12M6 12h12"/></svg>';

  function bindLines(root) {
    root.addEventListener('click', async (e) => {
      const line = e.target.closest('[data-line]'); if (!line) return;
      const input = $('[data-qty-input]', line);
      let q = null;
      if (e.target.closest('[data-qty]')) q = Math.max(0, Number(input.value) + Number(e.target.closest('[data-qty]').dataset.qty));
      if (e.target.closest('[data-remove]')) q = 0;
      if (q === null) return;
      line.style.opacity = '.5';
      Cart.publish(await Cart.change(Number(line.dataset.line), q));
    });
    root.addEventListener('change', async (e) => {
      if (!e.target.matches('[data-qty-input]')) return;
      const line = e.target.closest('[data-line]');
      Cart.publish(await Cart.change(Number(line.dataset.line), Math.max(0, Number(e.target.value) || 0)));
    });
  }

  class CartDrawer extends ModalDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.lines = $('[data-cart-lines]', this);
      this.footer = $('[data-cart-footer]', this);
      bindLines(this.lines);
      document.addEventListener('cart:updated', (e) => this.render(e.detail.cart));
      Cart.get().then((c) => Cart.publish(c)).catch(() => {});
    }
    render(cart) {
      this.lines.innerHTML = renderLines(cart);
      this.footer.hidden = !cart.item_count;
      $('[data-cart-subtotal]', this).textContent = money(cart.total_price);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.lines = $('[data-cart-lines]', this);
      this.footer = $('[data-cart-footer]', this);
      bindLines(this.lines);
      document.addEventListener('cart:updated', (e) => this.render(e.detail.cart));
      Cart.get().then((c) => this.render(c));
    }
    render(cart) {
      this.lines.innerHTML = renderLines(cart);
      this.footer.hidden = !cart.item_count;
      $('[data-cart-subtotal]', this).textContent = money(cart.total_price);
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------------------------------------------------------------- product form */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      this.button = $('[type=submit]', this.form);
      this.error = $('[data-form-error]', this);
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      if (this.hasAttribute('data-buy-now')) return this.buyNow(e);
      if (window.theme?.cartType !== 'drawer') return; // fall back to a normal POST to /cart/add
      e.preventDefault();
      if (!this.form.reportValidity()) return;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      try {
        await Cart.add(new FormData(this.form));
        const cart = await Cart.get();
        Cart.publish(cart);
        document.getElementById('CartDrawer')?.open(this.button);
      } catch (err) {
        if (this.error) { this.error.textContent = err.message; this.error.hidden = false; } else toast(err.message);
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
      }
    }
  }
  // "Buy now": puts this item in the cart exactly once (pressing it twice never doubles it up; anything
  // else already in the cart stays) and goes straight to Shopify's checkout. Without JS, or if this
  // fails, the form posts normally to /cart/add and its return_to sends the visitor to checkout.
  ProductForm.prototype.buyNow = async function (e) {
    e.preventDefault();
    if (!this.form.reportValidity()) return;
    this.button.classList.add('is-loading');
    this.button.setAttribute('aria-busy', 'true');
    try {
      const id = new FormData(this.form).get('id');
      const res = await fetch(`${routes.cart}/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: { [id]: 1 } }) });
      if (!res.ok) throw new Error();
      window.location.assign(`${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}checkout`);
    } catch {
      this.form.submit();
    }
  };
  customElements.define('product-form', ProductForm);

  /* Variant picker: reads the product's variants JSON (rendered by Liquid) */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('script[type="application/json"]', this).textContent);
      this.section = this.closest('[data-product-section]') || document;
      this.addEventListener('change', () => this.update());
    }
    update() {
      const selected = $$('fieldset', this).map((fs) => $('input:checked', fs)?.value);
      const v = this.variants.find((x) => x.options.every((o, i) => o === selected[i]));
      $$('[data-selected-value]', this).forEach((el, i) => { el.textContent = selected[i]; });
      if (!v) return;
      $$('input[name="id"]', this.section).forEach((i) => { i.value = v.id; });
      $$('[data-price-current]', this.section).forEach((el) => { el.textContent = money(v.price); });
      $$('[data-price-compare]', this.section).forEach((el) => {
        const onSale = v.compare_at_price && v.compare_at_price > v.price;
        el.hidden = !onSale; if (onSale) el.lastChild.textContent = money(v.compare_at_price);
      });
      $$('[data-add-button]', this.section).forEach((b) => {
        b.disabled = !v.available;
        const label = $('[data-add-label]', b); if (label) label.textContent = v.available ? label.dataset.available : label.dataset.soldout;
      });
      $$('[data-variant-note]', this.section).forEach((el) => { el.textContent = el.dataset[`n${v.options[0]}`] || el.dataset.default || ''; });
      const url = new URL(location.href); url.searchParams.set('variant', v.id);
      history.replaceState({}, '', url);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* Sticky mobile buy bar: appears once the main add-to-cart scrolls out of view */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(target);
    }
  }
  customElements.define('sticky-buy', StickyBuy);

  /* Mobile gallery: "1 / 2" counter follows the swipe position */
  $$('.product__gallery').forEach((g) => {
    const out = $('[data-slide-index]', g.parentElement);
    if (!out) return;
    g.addEventListener('scroll', debounce(() => { out.textContent = Math.round(g.scrollLeft / g.clientWidth) + 1; }, 60), { passive: true });
  });

  /* ---------------------------------------------------------------- spotify */
  // One embed per <spotify-player>. Loaded on first play (keeps Spotify's ~1MB off initial load).
  let spotifyApi;
  function loadSpotifyApi() {
    if (spotifyApi) return spotifyApi;
    spotifyApi = new Promise((resolve, reject) => {
      window.onSpotifyIframeApiReady = (api) => resolve(api);
      const s = document.createElement('script');
      s.src = 'https://open.spotify.com/embed/iframe-api/v1';
      s.async = true; s.onerror = reject;
      document.head.append(s);
      setTimeout(() => reject(new Error('timeout')), 6000);
    });
    return spotifyApi;
  }
  const uriToEmbed = (uri) => { const [, type, id] = String(uri).split(':'); return `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`; };

  class SpotifyPlayer extends HTMLElement {
    connectedCallback() {
      this.embed = $('[data-player-embed]', this);
      this.section = this.closest('section') || document;
      this.compact = this.hasAttribute('data-compact');
      this.uri = this.dataset.uri;
      $('[data-player-start]', this)?.addEventListener('click', () => this.play(this.uri));
      $('[data-player-close]', this)?.addEventListener('click', () => this.undock());
      this.section.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-play]');
        if (!btn || !this.section.contains(btn) || this.contains(btn)) return;
        this.play(btn.dataset.play, btn);
      });
    }
    async play(uri, trigger) {
      this.uri = uri;
      this.markActive(uri, trigger);
      if (!this.compact && window.innerWidth < 990) this.dock();
      this.classList.add('is-loaded');
      if (this.controller) { this.controller.loadUri(uri); this.controller.play(); return; }
      if (this.iframe) { this.iframe.src = uriToEmbed(uri); return; }
      const mount = document.createElement('div');
      this.embed.append(mount);
      try {
        const api = await loadSpotifyApi();
        api.createController(mount, { uri, height: this.compact || this.classList.contains('is-docked') ? 80 : 152, width: '100%' }, (controller) => {
          this.controller = controller;
          controller.addListener('ready', () => controller.play());
          controller.addListener('playback_update', (e) => {
            $$('.track.is-active', this.section).forEach((t) => t.classList.toggle('is-playing', !e.data.isPaused));
          });
        });
      } catch {
        mount.remove();
        this.iframe = Object.assign(document.createElement('iframe'), { src: uriToEmbed(uri), loading: 'lazy', allow: 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture', title: 'Spotify player' });
        this.embed.append(this.iframe);
      }
    }
    markActive(uri, trigger) {
      $$('.track', this.section).forEach((t) => {
        const on = $('[data-play]', t)?.dataset.play === uri;
        t.classList.toggle('is-active', on);
        if (!on) t.classList.remove('is-playing');
        $('[data-play]', t)?.setAttribute('aria-pressed', on);
      });
      const title = $('[data-player-title]', this); if (title && trigger) title.textContent = trigger.dataset.title;
      const meta = $('[data-player-meta]', this); if (meta && trigger) meta.textContent = trigger.dataset.meta || '';
    }
    dock() { this.classList.add('is-docked'); document.body.classList.add('has-docked-player'); const c = $('[data-player-close]', this); if (c) c.hidden = false; }
    undock() {
      this.controller?.pause?.();
      this.classList.remove('is-docked', 'is-loaded');
      document.body.classList.remove('has-docked-player');
      const c = $('[data-player-close]', this); if (c) c.hidden = true;
      this.controller?.destroy?.(); this.controller = null;
      this.iframe?.remove(); this.iframe = null; this.embed.innerHTML = '';
      $$('.track', this.section).forEach((t) => t.classList.remove('is-active', 'is-playing'));
    }
  }
  customElements.define('spotify-player', SpotifyPlayer);

  /* ---------------------------------------------------------------- search */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type=search]', this);
      this.results = $('[data-results]', this);
      this.initial = this.results.innerHTML;
      this.input.addEventListener('input', debounce(() => this.search(this.input.value.trim()), 220));
      this.addEventListener('click', (e) => {
        const s = e.target.closest('[data-suggest]'); if (!s) return;
        e.preventDefault(); this.input.value = s.dataset.suggest; this.search(s.dataset.suggest); this.input.focus();
      });
    }
    async search(q) {
      if (!q) { this.results.innerHTML = this.initial; return; }
      const url = `${routes.predictive_search}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
      const data = await (await fetch(url)).json();
      const items = data.resources?.results?.products || [];
      this.results.innerHTML = items.length
        ? items.map((p) => `<a class="predictive-item" href="${p.url}"><img src="${p.image}" alt="" width="64" height="64" loading="lazy"><span><span class="predictive-item__title">${escapeHTML(p.title)}</span><br><span class="predictive-item__type">${escapeHTML(p.type)}</span></span><span class="tabular small">${p.price_min !== p.price_max ? `From ${money(Math.round(p.price_min * 100))}` : money(Math.round(p.price * 100))}</span></a>`).join('')
          + `<p class="search-modal__hint"><a href="${routes.search}?q=${encodeURIComponent(q)}&type=product">See all results for “${escapeHTML(q)}”</a></p>`
        : `<p class="search-modal__hint">No results for “${escapeHTML(q)}”. Try <a href="#" data-suggest="piano">piano</a> or <a href="#" data-suggest="cinematic">cinematic</a>.</p>`;
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------------------------------------------------------------- reveal */
  // stagger lists: give each child its index for the CSS delay
  $$('[data-reveal="stagger"]').forEach((list) => [...list.children].forEach((child, i) => child.style.setProperty('--i', i)));
  if (document.body.classList.contains('has-reveal') && 'IntersectionObserver' in window && !reduceMotion) {
    // wipe/curtain elements start fully clipped (zero visible area), so the observer
    // watches their parent instead and reveals every element mapped to it.
    const watchers = new Map();
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (!en.isIntersecting) return;
      watchers.get(en.target).forEach((el) => el.classList.add('is-revealed'));
      io.unobserve(en.target);
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    $$('[data-reveal]').forEach((el) => {
      const clipped = el.dataset.reveal === 'wipe' || el.dataset.reveal === 'curtain';
      const target = clipped ? el.parentElement : el;
      if (!watchers.has(target)) { watchers.set(target, []); io.observe(target); }
      watchers.get(target).push(el);
    });
  } else {
    $$('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
  }
})();
